import React, { useMemo, Fragment } from "react";
import { useParams } from "react-router-dom";
import { APIError } from "../../components/APIError";
import {
  CloseButton,
  CustomDialog,
  Field,
  Loader,
} from "../../components/CustomDialog";
import { useFallbackUrl, useNavigator } from "../../components/AppHistory";
import { Charge } from "./components/Charge";
import { UnitRateCharge } from "./components/UnitRateCharge";

import {
  Box,
  Chip,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  styled,
  Typography,
  useTheme,
} from "@material-ui/core";
import { AgreementIcon, TickIcon } from "../../icons";
import { AgreementInfo, formatRecurringCharge } from "../../api/services";
import { useAgreementDetails } from "./hooks/useAgreementDetails";
import { formatDate, formatInclusiveDate } from "../../utils/formatDate";
import { formatMoneyWithSign } from "../../utils/formatMoney";
import { InlineType } from "../../components/InlineType";
import { ModalComponent } from "../../pages/routes";
import { TextBox } from "../../components/TextBox";

const ThickChip = styled(Chip)(() => ({
  borderWidth: "2px",
}));

export const AgreementDetails: ModalComponent = ({ fallbackUrl }) => {
  useFallbackUrl(fallbackUrl);
  const { accountNumber, agreementId } = useParams();
  const { closeOverlay } = useNavigator();
  const { data, error, isLoading } = useAgreementDetails(
    accountNumber,
    agreementId,
  );
  const agreement = data || ({} as AgreementInfo);

  const agreementDates = useMemo(() => {
    if (!agreement?.fromDttm) return "";
    if (agreement.toDttm != null) {
      return `${formatDate(agreement.fromDttm)} to ${formatInclusiveDate(
        agreement.toDttm,
      )}`;
    } else {
      return `${formatDate(agreement.fromDttm)} onwards`;
    }
  }, [agreement]);

  const isDisplayOrderDttm = useMemo(() => {
    if (agreement.orderDttm && agreement.createdDttm) {
      return (
        agreement.orderDttm.split("T")[0] !==
        agreement.createdDttm.split("T")[0]
      );
    }
    /* istanbul ignore next */
    return false;
  }, [agreement.orderDttm, agreement.createdDttm]);

  const isCancelled = agreement?.cancelledDttm != null;
  const theme = useTheme();

  return (
    <CustomDialog onClose={closeOverlay}>
      <DialogTitle>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="no-wrap"
        >
          <Box>
            <Box display="flex" alignItems="center">
              <Box mr={0.5} lineHeight={1} color="text.primary">
                <AgreementIcon
                  height={24}
                  width={24}
                  style={{
                    fill: isCancelled
                      ? theme.palette.text.secondary
                      : "currentColor",
                  }}
                />
              </Box>
              <TextBox isStruckThrough={isCancelled} isGrey={isCancelled}>
                <Typography variant="h6">Agreement</Typography>
              </TextBox>
            </Box>
            <Box>
              <Typography
                variant="body1"
                color={isCancelled ? "textSecondary" : "textPrimary"}
              >
                {agreement?.name}
              </Typography>
            </Box>
          </Box>
          <Box mr={-1.5}>
            <CloseButton data-testid="AgreementDetails__Button--close" />
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {error ? (
          <Box pb={45.5}>
            <APIError title="We can't load details for this agreement at the moment." />
          </Box>
        ) : isLoading ? (
          <Loader data-testid="AgreementDetails__Loader" />
        ) : (
          agreement && (
            <>
              <Box mb={2}>
                {isCancelled && (
                  <>
                    <Box mb={2} data-testid="AgreementDetails__Box--cancelled">
                      <ThickChip
                        label={
                          <Typography variant="caption" color="textPrimary">
                            <InlineType fontWeight="bold">
                              Agreement cancelled
                            </InlineType>
                          </Typography>
                        }
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                    <Box mb={2}>
                      <Typography variant="body2" color="textPrimary">
                        <InlineType fontWeight="bold">
                          Cancelled by {agreement.cancelledUser} on{" "}
                          {formatDate(agreement.cancelledDttm)}
                        </InlineType>
                      </Typography>
                    </Box>
                  </>
                )}
                {agreement?.toDttm &&
                  new Date(agreement?.toDttm) < new Date() &&
                  !isCancelled && (
                    <Box mb={2} data-testid="AgreementDetails__Box--ended">
                      <ThickChip
                        label={
                          <Typography variant="caption" color="textPrimary">
                            <InlineType fontWeight="bold">
                              Agreement ended
                            </InlineType>
                          </Typography>
                        }
                        color="secondary"
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  )}
                <Grid container justify="space-between">
                  <Field
                    label="Agreement dates"
                    data-testid="Field__AgreementDates--value"
                    value={agreementDates}
                  />
                  <Field
                    label="Created"
                    data-testid="Field__Created--value"
                    value={formatDate(agreement.createdDttm)}
                  />
                  <Field
                    label="Order date"
                    data-testid="Field__OrderDt--value"
                    value={
                      isDisplayOrderDttm
                        ? formatDate(agreement.orderDttm)
                        : undefined
                    }
                    hideEmpty
                  />
                  <Field
                    label="Contracted to"
                    data-testid="Field__ContractedTo--value"
                    value={formatDate(agreement.contractedToDttm)}
                    hideEmpty
                  />
                  <Field
                    label="Agreement number"
                    data-testid="Field__Number--value"
                    value={agreement.number}
                  />
                  <Field
                    label="Follow on"
                    data-testid="Field__FollowOn--value"
                    value={
                      agreement?.followOn && (
                        <Box ml={-0.45} clone>
                          <TickIcon style={{ fill: "currentColor" }} />
                        </Box>
                      )
                    }
                    hideEmpty
                  />
                  <Field
                    label="Reference"
                    data-testid="Field__Reference--value"
                    value={agreement.reference}
                    hideEmpty
                  />
                  <Field
                    label="Broker agent"
                    data-testid="Field__Broker--value"
                    value={agreement.brokerAgent}
                    hideEmpty
                  />
                  <Field
                    label="Purchasing group"
                    data-testid="Field__PurchasingGroup--value"
                    value={agreement.purchasingGroup}
                    hideEmpty
                  />
                  <Field
                    label="Termination received"
                    data-testid="Field__TerminationDt--value"
                    value={formatDate(agreement.terminationReceivedDt)}
                    hideEmpty
                  />
                  <Field
                    label="Termination reason"
                    data-testid="Field__TerminationReason--value"
                    value={agreement.terminationReason}
                    hideEmpty
                  />
                  <Field
                    label="Bill display name"
                    data-testid="Field__BillDisplay--value"
                    value={agreement.display}
                    hideEmpty
                  />
                </Grid>
              </Box>
              <Box>
                <Box mb={1.25}>
                  <Typography variant="overline">PRICING</Typography>
                </Box>
              </Box>
              <Divider />
              {agreement.tariff ? (
                <>
                  <Box mb={2} data-testid="AgreementDetails__Tariff">
                    {agreement.tariff?.unitRate && (
                      <UnitRateCharge {...agreement.tariff.unitRate} />
                    )}
                    {agreement.tariff?.standingCharge && (
                      <Charge
                        name="Standing charge"
                        price={
                          agreement.tariff?.standingCharge
                            ? formatRecurringCharge({
                                value: agreement.tariff.standingCharge.value,
                                currency: agreement.currency,
                                chargeFrequency:
                                  agreement.tariff.standingCharge
                                    .chargeFrequency,
                              })
                            : null
                        }
                      />
                    )}
                    <Divider />
                    {agreement.tariff.otherUnitRates.map((unitRate) => (
                      <UnitRateCharge {...unitRate} key={unitRate.id} />
                    ))}
                    {agreement.tariff.otherRecurringCharges
                      .filter((charge) => !charge.filtered)
                      .map((recurringCharge) => (
                        <Fragment key={recurringCharge.id}>
                          <Charge
                            name={recurringCharge.name}
                            price={formatRecurringCharge({
                              value: recurringCharge.value,
                              currency: agreement.currency,
                              chargeFrequency: recurringCharge.chargeFrequency,
                            })}
                          />
                          <Divider />
                        </Fragment>
                      ))}
                    {agreement.tariff.earlyTerminationCharge &&
                      !agreement.tariff.earlyTerminationCharge.filtered && (
                        <>
                          <Charge
                            name="Early termination charge"
                            price={formatMoneyWithSign(
                              agreement.currency,
                              agreement.tariff.earlyTerminationCharge.value,
                            )}
                          />
                          <Divider />
                        </>
                      )}
                  </Box>
                  <Box width={1} pr={2} textAlign="right">
                    <Typography variant="caption" color="textPrimary">
                      {agreement.tariff.taxInclusive
                        ? "All prices include sales tax"
                        : "All prices exclude sales tax"}
                    </Typography>
                  </Box>
                </>
              ) : (
                <Box
                  data-testid="AgreementDetails__Tariff--empty"
                  mt={1}
                  mb={1}
                >
                  No tariff details for this agreement.
                </Box>
              )}
            </>
          )
        )}
      </DialogContent>
    </CustomDialog>
  );
};
